/**
 * AskClaude — floating help button + chat side panel.
 *
 * Mounted globally in Layout so it's available on every authenticated
 * page. Click the bottom-right "?" button to open a side panel; ask
 * questions about Transaction Rocket and Claude answers, with the
 * current page URL passed as context so the answer is specific.
 *
 * v1 behavior:
 *   - In-memory history per session (resets on full page reload, NOT on
 *     route change so the conversation survives navigation).
 *   - Slides in from the right at 400px wide on desktop, full-width on
 *     mobile.
 *   - Esc closes; click backdrop closes.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { askClaude, type AskMessage } from '../api/help';
import { Icon } from './Icon';

export default function AskClaude() {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState<AskMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Esc closes the panel.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Auto-scroll to latest message.
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, open]);

  async function send() {
    const text = draft.trim();
    if (!text || sending) return;
    setError(null);
    setDraft('');
    const next: AskMessage[] = [...history, { role: 'user', content: text }];
    setHistory(next);
    setSending(true);
    try {
      const pageTitle = document.title || undefined;
      const res = await askClaude({
        message: text,
        history: next,
        page_url: location.pathname,
        page_title: pageTitle,
      });
      setHistory([...next, { role: 'assistant', content: res.reply }]);
    } catch (e) {
      setError((e as Error).message || 'Something went wrong.');
      // Roll back the user message so they can retry without retyping.
      setHistory(history);
      setDraft(text);
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      {/* Floating button — always visible while signed in. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Ask Claude for help"
        title="Ask Claude — anything about Transaction Rocket"
        // The class is the mobile lift hook — see .tr-ask-claude-fab in
        // index.css. Without it the agent bottom nav buries this button.
        className="tr-ask-claude-fab"
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          width: 52,
          height: 52,
          borderRadius: '50%',
          border: 'none',
          background: 'var(--brand-primary)',
          color: '#fff',
          cursor: 'pointer',
          boxShadow: '0 6px 20px rgba(31, 29, 61, 0.25)',
          display: open ? 'none' : 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 90,
          transition: 'transform 120ms',
        }}
        onMouseEnter={(e) => {
          if (e.currentTarget) e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          if (e.currentTarget) e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <Icon name="sparkles" size={22} />
      </button>

      {open && (
        <>
          {/* Backdrop — tap to close on mobile. Transparent on desktop. */}
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 14, 34, 0.18)',
              zIndex: 89,
            }}
          />
          {/* Panel — slides in from the right. */}
          <aside
            role="dialog"
            aria-label="Ask Claude"
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: 'min(420px, 100vw)',
              background: 'var(--bg-surface)',
              borderLeft: '1px solid var(--border)',
              boxShadow: '-8px 0 24px rgba(15, 14, 34, 0.12)',
              zIndex: 90,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header */}
            <header
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '14px 16px',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <span
                style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: 'linear-gradient(135deg, var(--brand-primary) 0%, var(--accent-coral, #F08C7F) 100%)',
                  color: '#fff',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Icon name="sparkles" size={15} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, color: 'var(--text-heading)' }}>
                  Ask Claude
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  Anything about Transaction Rocket
                </div>
              </div>
              {history.length > 0 && (
                <button
                  type="button"
                  onClick={() => { setHistory([]); setError(null); }}
                  title="Clear conversation"
                  style={iconBtnStyle}
                >
                  <Icon name="alert-circle" size={14} />
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                style={iconBtnStyle}
              >
                <Icon name="chevron-right" size={14} />
              </button>
            </header>

            {/* Messages */}
            <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {history.length === 0 && <EmptyState onPick={(s) => setDraft(s)} />}
              {history.map((m, i) => (
                <MessageBubble key={i} role={m.role} content={m.content} />
              ))}
              {sending && <MessageBubble role="assistant" content="…" muted />}
              {error && (
                <div
                  role="alert"
                  style={{
                    background: 'var(--danger-soft, #FCE7E7)',
                    color: 'var(--danger, #C03A3A)',
                    padding: '10px 12px',
                    borderRadius: 10,
                    fontSize: 12,
                  }}
                >
                  {error}
                </div>
              )}
            </div>

            {/* Composer */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              style={{
                borderTop: '1px solid var(--border)',
                padding: 12,
                display: 'flex',
                gap: 8,
                background: 'var(--bg-surface)',
              }}
            >
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.currentTarget?.value ?? '')}
                onKeyDown={(e) => {
                  // Enter to send, Shift+Enter for newline.
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Ask anything — &quot;how do I add a co-agent?&quot;"
                rows={2}
                disabled={sending}
                style={{
                  flex: 1,
                  minHeight: 38,
                  maxHeight: 140,
                  padding: '8px 10px',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  color: 'var(--text-body)',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  resize: 'none',
                  outline: 'none',
                }}
              />
              <button
                type="submit"
                disabled={sending || !draft.trim()}
                style={{
                  alignSelf: 'flex-end',
                  padding: '8px 14px',
                  background: 'var(--brand-primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: sending || !draft.trim() ? 'not-allowed' : 'pointer',
                  opacity: sending || !draft.trim() ? 0.6 : 1,
                  fontFamily: 'inherit',
                }}
              >
                {sending ? '…' : 'Send'}
              </button>
            </form>
          </aside>
        </>
      )}
    </>
  );
}

function EmptyState({ onPick }: { onPick: (s: string) => void }) {
  const suggestions = [
    'How do I create a sign-up form?',
    'What\'s a Smart Plan?',
    'How do I duplicate a form for another agent?',
    'Where do I edit my voice profile?',
  ];
  return (
    <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ fontSize: 13, color: 'var(--text-body)', margin: 0, lineHeight: 1.5 }}>
        Hey! I can answer questions about Transaction Rocket — features, where things live,
        how to do specific tasks. I know what page you're on so my answers will be
        specific to where you are right now.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Try asking…
        </div>
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onPick(s)}
            style={{
              textAlign: 'left',
              padding: '8px 12px',
              background: 'var(--bg-surface-2)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              color: 'var(--text-heading)',
              fontSize: 12,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({
  role,
  content,
  muted,
}: {
  role: 'user' | 'assistant';
  content: string;
  muted?: boolean;
}) {
  const isUser = role === 'user';
  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      <div
        style={{
          maxWidth: '85%',
          padding: '10px 12px',
          borderRadius: 12,
          fontSize: 13,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          background: isUser ? 'var(--brand-primary)' : 'var(--bg-surface-2)',
          color: isUser ? '#fff' : muted ? 'var(--text-muted)' : 'var(--text-body)',
          border: isUser ? 'none' : '1px solid var(--border)',
        }}
      >
        {content}
      </div>
    </div>
  );
}

const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  background: 'transparent',
  border: 'none',
  borderRadius: 6,
  color: 'var(--text-muted)',
  cursor: 'pointer',
};
